import { Document } from '@contentful/rich-text-types'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import * as React from 'react'

import { ScreenReaderOnly, TypeStyle } from '@syconium/little-miss-figgy'
import { ProgressiveAsset } from '@syconium/little-miss-figgy'

import { useTranslation } from '../../../app/_providers/TranslationProvider.client'
import {
  modalAnimationDuration,
  standardModalBodyOpenClassName,
  standardModalPortalClassName,
} from '../../../brunswick/styles/constants'
import { useProductHref } from '../../../lib/routing'
import { resizeShopifyImageSource } from '../../../lib/utils'
import { Product } from '../../../types/graphql'
import { FitModelStateContainer } from '../../pages/products/ProductPage/contexts/FitModelStateContainer'
import { CollectionLinks } from '../../pages/products/ProductPage/DetailsSection/CollectionLink'
import { ProductDetailsAccordionSection } from '../../pages/products/ProductPage/DetailsSection/ProductDetailsAccordionSection'
import { NextModal } from '../NextModal'

import {
  Body,
  Content,
  FullDetailsButton,
  Header,
  ImageWrapper,
  ModalDismiss,
  Summary,
  TextWrapper,
  modalStyles,
} from './styles'

export interface ProductDetailsModalProps {
  closeModal: () => void
  isOpen: boolean
  product: Product
  productGroupRtfFeatures?: Document
  title: string
}

export const ProductDetailsModal: React.FC<ProductDetailsModalProps> = ({
  closeModal,
  isOpen,
  product,
  productGroupRtfFeatures,
  title,
}) => {
  const {
    detailsSection: {
      detailsModal: { title: modalTitle, viewFullDetails },
    },
  } = useTranslation()

  // plain=true query param is used by iOS app to view webpages within a webview without the website's header/footer
  const plainQueryParamValue = useSearchParams()?.get('plain') ?? undefined

  const {
    careInstructions,
    fitModel,
    fitModelsDescriptions,
    handle,
    images,
    rawMaterials,
    rtfFeatures: productRtfFeatures,
    collectionLinks,
  } = product

  const href = useProductHref(handle || '', { plain: plainQueryParamValue })
  const image = images?.nodes[0]
  const imageSize = 520

  return (
    <FitModelStateContainer.Provider
      initialState={{
        fitModel: fitModel ?? undefined,
      }}
    >
      <NextModal
        bodyOpenClassName={standardModalBodyOpenClassName}
        closeTimeoutMS={modalAnimationDuration}
        isOpen={isOpen}
        onRequestClose={closeModal}
        portalClassName={standardModalPortalClassName}
        style={modalStyles}
      >
        <Body>
          <Header>
            <ScreenReaderOnly>{modalTitle}</ScreenReaderOnly>
            <ModalDismiss onClick={closeModal} />
          </Header>

          <Content>
            {image?.source && (
              <ImageWrapper>
                <ProgressiveAsset.Image
                  alt={title}
                  src={resizeShopifyImageSource({
                    width: imageSize,
                    height: imageSize,
                    source: image.source,
                  })}
                />
              </ImageWrapper>
            )}
            <TextWrapper>
              <Summary>
                <TypeStyle.HeadlineSmall>{title}</TypeStyle.HeadlineSmall>
                {href && (
                  <Link href={href}>
                    <FullDetailsButton>{viewFullDetails}</FullDetailsButton>
                  </Link>
                )}
              </Summary>
              <ProductDetailsAccordionSection
                careInstructions={careInstructions}
                fitModelsDescriptions={fitModelsDescriptions}
                productRtfFeatures={productRtfFeatures}
                productGroupRtfFeatures={productGroupRtfFeatures}
                rawMaterials={rawMaterials}
              />
              <CollectionLinks links={collectionLinks?.nodes || []} />
            </TextWrapper>
          </Content>
        </Body>
      </NextModal>
    </FitModelStateContainer.Provider>
  )
}

export default ProductDetailsModal
